// src/components/BlogManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { formatBlogPublishDate, getBlogPublishTimestamp } from '../utils/blogPublishDate';
import { updateSitemap } from '../utils/sitemapUpdater';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const triggerRebuild = () => {
  if (!SITE_CONFIG.AMPLIFY_REBUILD_WEBHOOK) return;
  fetch(SITE_CONFIG.AMPLIFY_REBUILD_WEBHOOK, { method: 'POST', mode: 'no-cors' }).catch(() => {});
};

const BlogManager = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newBlog, setNewBlog] = useState({
    title: '',
    text: '',
    metaDescription: '',
    imageFile: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [blogContent, setBlogContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isFuturePost, setIsFuturePost] = useState(false);
  const [futurePostDate, setFuturePostDate] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-blog?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blogsArray = Array.isArray(data) ? data : [];
        // Sort blogs by created_at date, newest first
        const sortedBlogs = blogsArray.sort((a, b) => getBlogPublishTimestamp(b) - getBlogPublishTimestamp(a));
        setBlogs(sortedBlogs);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const handleCreateBlog = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let blogPayload = {
        site_id: SITE_CONFIG.SITE_ID,
        title: newBlog.title,
        text: newBlog.text,
        meta_description: newBlog.metaDescription
      };

      if (editingBlog) {
        blogPayload.file_id = editingBlog.file_id;
      }

      if (isFuturePost && futurePostDate) {
        blogPayload.future_post_date = futurePostDate;
      }

      // Upload image if provided
      if (newBlog.imageFile) {
        setUploadingImage(true);
        const base64Data = await convertToBase64(newBlog.imageFile);
        
        const imageResponse = await fetch('https://tophamtechbe.com/api/treas/blogs/v1/store-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'treas-api-key-2025'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            filename: newBlog.imageFile.name,
            image_data: base64Data
          })
        });

        if (imageResponse.ok) {
          const imageData = await imageResponse.json();
          blogPayload.s3_blog_image_file_id = imageData.file_id;
          blogPayload.s3_blog_image_s3key = imageData.filename;
        }
        setUploadingImage(false);
      } else if (editingBlog && editingBlog.s3_blog_image_file_id) {
        blogPayload.s3_blog_image_file_id = editingBlog.s3_blog_image_file_id;
        blogPayload.s3_blog_image_s3key = editingBlog.s3_blog_image_s3key;
      }

      const url = editingBlog 
        ? 'https://tophamtechbe.com/api/treas/blogs/v1/update-blog'
        : 'https://tophamtechbe.com/api/treas/blogs/v1/store-blog';
      
      const method = editingBlog ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify(blogPayload)
      });

      if (response.ok) {
        setNewBlog({ title: '', text: '', metaDescription: '', imageFile: null });
        setIsFuturePost(false);
        setFuturePostDate('');
        setEditingBlog(null);
        setShowCreateForm(false);
        setShowSuccessModal(true);
        fetchBlogs();
        updateSitemap();
        triggerRebuild();
      } else {
        alert(`Failed to ${editingBlog ? 'update' : 'create'} blog`);
      }
    } catch (error) {
      console.error(`Error ${editingBlog ? 'updating' : 'creating'} blog:`, error);
      alert(`Failed to ${editingBlog ? 'update' : 'create'} blog`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExpandText = async () => {
    const wordCount = newBlog.text.trim().split(/\s+/).length;
    if (wordCount > 150) return;
    
    setExpanding(true);
    try {
      const response = await fetch('https://tophamtechbe.com/llm/v1/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: `Clean up and Expand on this text, make it into a real estate blog: ${newBlog.text}`,
          site_id: SITE_CONFIG.SITE_ID
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewBlog({...newBlog, text: data.response});
        // Update the contentEditable div
        const editor = document.querySelector('[contenteditable]');
        if (editor && data.response) {
          editor.innerHTML = data.response;
        }
      } else {
        alert('Failed to expand text');
      }
    } catch (error) {
      console.error('Error expanding text:', error);
      alert('Failed to expand text');
    } finally {
      setExpanding(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!blogToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/delete-blog?site_id=${SITE_CONFIG.SITE_ID}&file_id=${blogToDelete.file_id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        fetchBlogs();
        setShowDeleteModal(false);
        setBlogToDelete(null);
        if (selectedBlog?.id === blogToDelete.id) {
          setSelectedBlog(null);
          setBlogContent('');
        }
        updateSitemap(); // Update sitemap after blog deletion
        triggerRebuild();
      } else {
        alert('Failed to delete blog');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog');
    } finally {
      setDeleting(false);
    }
  };

  const fetchBlogContent = async (url) => {
    setLoadingContent(true);
    console.log('Fetching blog from URL:', url);
    try {
      const response = await fetch(url);
      console.log('Response status:', response.status);
      if (response.ok) {
        const html = await response.text();
        setBlogContent(html);
      } else {
        console.log('Using iframe src instead due to CORS');
        setBlogContent(`<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`);
      }
    } catch (error) {
      console.error('CORS error, using iframe src:', error);
      setBlogContent(`<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`);
    } finally {
      setLoadingContent(false);
    }
  };

  return (
    <div className="admin-dashboard-modern">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Blog Management</h1>
            <p className="dashboard-subtitle">Create, manage, and publish blog content for your real estate website</p>
          </div>
          <div className="header-right">
            <button 
              onClick={() => setShowCreateForm(true)} 
              className="create-blog-btn-modern"
            >
              Create Blog Post
            </button>
            <button onClick={() => navigate('/admin')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main-content">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#1B96FF'}}>📝</div>
            <div className="stat-content">
              <div className="stat-number">{blogs.length}</div>
              <div className="stat-label">Total Posts</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#4CAF50'}}>📅</div>
            <div className="stat-content">
              <div className="stat-number">{blogs.filter(b => new Date(b.created_at) > new Date(Date.now() - 30*24*60*60*1000)).length}</div>
              <div className="stat-label">This Month</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#FF9800'}}>🔗</div>
            <div className="stat-content">
              <div className="stat-number">{blogs.length > 0 ? 'Active' : 'None'}</div>
              <div className="stat-label">Blog Status</div>
            </div>
          </div>
        </div>

        {/* Blog Management Section */}
        <div className="blog-management-section">
          <div className="management-grid">
            {/* Blog List */}
            <div className="blog-list-modern">
              <div className="section-header">
                <h2 className="section-title">Blog Posts</h2>
              </div>
              
              <div className="blog-list-content">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading blog posts...</p>
                  </div>
                ) : blogs.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📝</div>
                    <h3>No blog posts yet</h3>
                    <p>Create your first blog post to get started</p>
                  </div>
                ) : (
                  <div className="blog-items-list">
                    {blogs.map((blog, index) => (
                      <div 
                        key={index} 
                        className={`blog-item-modern ${selectedBlog === blog ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedBlog(blog);
                          fetchBlogContent(blog.url);
                        }}
                      >
                        <div className="blog-item-content">
                          <h3 className="blog-item-title">{blog.title}</h3>
                          <div className="blog-item-meta">
                            <span className="blog-item-date">
                              {formatBlogPublishDate(blog, { month: 'short' })}
                            </span>
                            <span className="blog-item-status" style={blog.future_post_date && new Date(blog.future_post_date) > new Date() ? {background: '#1B96FF'} : {}}>{blog.future_post_date && new Date(blog.future_post_date) > new Date() ? `Future Post Date: ${new Date(blog.future_post_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}` : 'Published'}</span>
                          </div>
                        </div>
                        <div className="blog-item-actions">
                          <button 
                            className="action-btn edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingBlog(blog);
                              setNewBlog({
                                title: blog.title,
                                text: blog.text_content || '',
                                metaDescription: blog.meta_description || '',
                                imageFile: null
                              });
                              setShowCreateForm(true);
                              // Set the contentEditable div content after modal opens
                              setTimeout(() => {
                                const editor = document.querySelector('[contenteditable]');
                                if (editor && blog.text_content) {
                                  editor.innerHTML = blog.text_content;
                                }
                              }, 100);
                            }}
                            title="Edit blog"
                            style={{background: 'none', color: '#1B96FF', padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500'}}
                          >
                            Edit
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBlogToDelete(blog);
                              setShowDeleteModal(true);
                            }}
                            title="Delete blog"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Blog Preview */}
            <div className="blog-preview-modern">
              <div className="section-header">
                <h2 className="section-title">Preview</h2>
                {selectedBlog && (
                  <a 
                    href={`/blog/${selectedBlog.id}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="preview-link"
                  >
                    View Live →
                  </a>
                )}
              </div>
              
              <div className="preview-content">
                {selectedBlog ? (
                  <div className="preview-wrapper">
                    {loadingContent ? (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading preview...</p>
                      </div>
                    ) : (
                      <iframe
                        src={selectedBlog.url}
                        title="Blog Preview"
                        className="blog-preview-iframe"
                      />
                    )}
                  </div>
                ) : (
                  <div className="no-selection-state">
                    <div className="no-selection-icon">👁️</div>
                    <h3>No blog selected</h3>
                    <p>Select a blog post from the list to preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => { setShowCreateForm(false); setEditingBlog(null); }}>
          <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{margin: 0}}>{editingBlog ? 'Edit Blog' : 'Create New Blog'}</h3>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <input
                  type="checkbox"
                  id="futurePost"
                  checked={isFuturePost}
                  onChange={(e) => setIsFuturePost(e.target.checked)}
                />
                <label htmlFor="futurePost" style={{margin: 0}}>Future Post</label>
                {isFuturePost && (
                  <input
                    type="date"
                    value={futurePostDate}
                    onChange={(e) => setFuturePostDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{marginLeft: '10px'}}
                  />
                )}
              </div>
            </div>
            <form onSubmit={handleCreateBlog}>
              <input
                type="text"
                placeholder="Blog Title"
                value={newBlog.title}
                onChange={(e) => setNewBlog({...newBlog, title: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Meta Description (150-160 characters recommended)"
                value={newBlog.metaDescription}
                onChange={(e) => setNewBlog({...newBlog, metaDescription: e.target.value})}
                maxLength="160"
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setNewBlog({...newBlog, imageFile: e.target.files[0]})}
              />
              {(newBlog.imageFile || (editingBlog && editingBlog.image_url)) && (
                <div style={{marginTop: '10px'}}>
                  <img 
                    src={newBlog.imageFile ? URL.createObjectURL(newBlog.imageFile) : editingBlog.image_url} 
                    alt="Preview" 
                    style={{width: '150px', height: '100px', objectFit: 'cover', borderRadius: '4px'}}
                  />
                  <div style={{fontSize: '14px', color: '#666', marginTop: '5px'}}>
                    {newBlog.imageFile ? newBlog.imageFile.name : 'Current image'}
                  </div>
                </div>
              )}
              <div className="textarea-container">
                <label style={{display: 'block', marginBottom: '10px', fontWeight: 'bold'}}>Blog Content *</label>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderBottom: 'none',
                  borderRadius: '4px 4px 0 0'
                }}>
                  <button 
                    type="button" 
                    onClick={handleExpandText}
                    disabled={expanding || !newBlog.text.trim() || newBlog.text.replace(/<[^>]*>/g, '').trim().split(/\s+/).length > 150}
                    style={{
                      padding: '6px 12px',
                      background: expanding ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: expanding ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {expanding ? 'Expanding...' : `🔍 Expand (${newBlog.text.replace(/<[^>]*>/g, '').trim().split(/\s+/).length}/150)`}
                  </button>
                  <div style={{width: '1px', height: '20px', background: '#dee2e6'}}></div>
                  <button type="button" onClick={() => document.execCommand('bold')} style={{padding: '6px 10px', border: '1px solid #dee2e6', background: 'white', borderRadius: '3px'}}><strong>B</strong></button>
                  <button type="button" onClick={() => document.execCommand('italic')} style={{padding: '6px 10px', border: '1px solid #dee2e6', background: 'white', borderRadius: '3px'}}><em>I</em></button>
                  <button type="button" onClick={() => document.execCommand('underline')} style={{padding: '6px 10px', border: '1px solid #dee2e6', background: 'white', borderRadius: '3px'}}><u>U</u></button>
                  <div style={{width: '1px', height: '20px', background: '#dee2e6'}}></div>
                  <button type="button" onClick={() => document.execCommand('insertUnorderedList')} style={{padding: '6px 10px', border: '1px solid #dee2e6', background: 'white', borderRadius: '3px'}}>• List</button>
                  <button type="button" onClick={() => document.execCommand('insertOrderedList')} style={{padding: '6px 10px', border: '1px solid #dee2e6', background: 'white', borderRadius: '3px'}}>1. List</button>
                  <div style={{width: '1px', height: '20px', background: '#dee2e6'}}></div>
                  <input type="color" onChange={(e) => document.execCommand('foreColor', false, e.target.value)} style={{width: '35px', height: '30px', border: '1px solid #dee2e6', borderRadius: '3px', cursor: 'pointer', padding: '2px'}} title="Text Color" />
                  <select onChange={(e) => document.execCommand('fontSize', false, e.target.value)} style={{padding: '6px', border: '1px solid #dee2e6', borderRadius: '3px', background: 'white'}}>
                    <option value="">Size</option>
                    <option value="1">Small</option>
                    <option value="3">Normal</option>
                    <option value="5">Large</option>
                    <option value="7">XLarge</option>
                  </select>
                </div>

                <div
                  contentEditable
                  onInput={(e) => setNewBlog({...newBlog, text: e.target.innerHTML})}
                  suppressContentEditableWarning={true}
                  style={{
                    border: '1px solid #e9ecef',
                    borderTop: 'none',
                    padding: '15px',
                    minHeight: '120px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: '#fff',
                    borderRadius: '0 0 4px 4px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    outline: 'none',
                    textAlign: 'left'
                  }}
                >
                  {!newBlog.text && <span style={{color: '#999', pointerEvents: 'none'}}>Start writing your blog post here... Aim for 2-6 paragraphs with engaging content.</span>}
                </div>
                
                <div style={{
                  marginTop: '15px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600'}}>
                    Blog Writing Guidelines
                  </h4>
                  <ul style={{margin: '0', paddingLeft: '20px', fontSize: '13px'}}>
                    <li style={{marginBottom: '6px'}}>Write 2-6 engaging paragraphs</li>
                    <li style={{marginBottom: '6px'}}>Use clear, conversational language</li>
                    <li style={{marginBottom: '6px'}}>Include relevant real estate insights</li>
                    <li style={{marginBottom: '6px'}}>Keep paragraphs concise and focused</li>
                    <li>Use formatting to improve readability</li>
                  </ul>
                </div>
              </div>
              <div className="modal-buttons">
                <button type="submit" disabled={submitting || uploadingImage}>
                  {uploadingImage ? 'Uploading Image...' : submitting ? (editingBlog ? 'Updating...' : 'Creating...') : (editingBlog ? 'Update Blog' : 'Create Blog')}
                </button>
                <button type="button" onClick={() => { setShowCreateForm(false); setEditingBlog(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✅</div>
            <h3>Blog {editingBlog ? 'Updated' : 'Created'} Successfully!</h3>
            <p>Your blog has been {editingBlog ? 'updated' : 'created'} and is now available for viewing.</p>
            <button 
              className="success-close-btn"
              onClick={() => setShowSuccessModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Blog</h3>
            <p>Are you sure you want to delete this blog?</p>
            <div className="blog-preview">
              "{blogToDelete?.title}"
            </div>
            <p>This will permanently remove the blog from S3 and its URL path. This action cannot be undone.</p>
            <div className="modal-buttons">
              <button 
                onClick={handleDeleteBlog}
                className="delete-confirm-btn"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Blog'}
              </button>
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setBlogToDelete(null);
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;